import React, { useCallback, useMemo } from 'react';
import { format, startOfDay, isAfter, isSameDay } from 'date-fns';
import { Game, GameTeam } from 'modules/games/domain/interfaces/Game';
import UDText from 'modules/ud-ui/components/text';
import * as S from './styles';
import { Team } from 'modules/teams/domain/interfaces/Team';
import TeamLogo from 'modules/ud-ui/components/team-logo';
import EditIcon from 'modules/ud-ui/components/icon/edit-icon';
import CupIcon from 'modules/ud-ui/components/icon/cup-icon';
import TrashIcon from 'modules/ud-ui/components/icon/trash-icon';
import { useModal } from '../../../../ud-ui/components/modal/useModal';
import UDButton from '../../../../ud-ui/components/button';

type GameTeamProps = {
  team: GameTeam | null;
  className?: string;
};

const GameTeamInfo = ({ team, className }: GameTeamProps) => {
  const isLeft = className === 'left';
  const iconFirst = !isLeft;
  const hasScore = !!team && team.score !== null;
  const infoNode = (<TeamInfo team={team} iconFirst={iconFirst} />);

  return (
    <S.TeamContainer className={className}>
      {isLeft && infoNode}
      {hasScore && (
        <S.GameTeamScore className='score'>
          {team?.score}
        </S.GameTeamScore>
      )}
      {!isLeft && infoNode}
    </S.TeamContainer>
  );
};

type TeamInfoProps = {
  team: Team | null;
  iconFirst?: boolean;
};

const defaultTeamInfoProps = {
  iconFirst: true,
};

const TeamInfo = (props: TeamInfoProps) => {
  const allProps = {
    ...defaultTeamInfoProps,
    ...props,
  };

  const {
    iconFirst,
    team,
  } = allProps;

  const teamName = team?.name || 'Неизвестно';
  const teamLogo = team?.logo?.url;

  if (iconFirst) {
    return (
      <>
        <TeamLogo
          className='mr-2'
          url={teamLogo}
        />
        <UDText type="subhead">{teamName}</UDText>
      </>
    );
  }

  return (
    <>
      <UDText type="subhead">{teamName}</UDText>
      <TeamLogo
        className='ml-2'
        url={teamLogo}
      />
    </>
  );
};

const TeamsDelimiter = () => (
  <S.TeamsDelimiterContainer>
    <S.TeamsDelimiter />
  </S.TeamsDelimiterContainer>
);

type GamesComponentsGameCardProps = {
  game: Game;
  onEditClick: (game: Game) => void;
  onShowResultClick?: (gameId: number) => void;
  onDeleteClick?: (gameId: number) => void;
} & Omit<DivProps, 'children'>;

const GamesComponentsGameCard = (props: GamesComponentsGameCardProps) => {
  const {
    game,
    onEditClick,
    onShowResultClick,
    onDeleteClick,
    ...containerProps
  } = props;

  const {
    open: openDeleteModal,
    close: closeDeleteModal,
    Modal: ConfirmDeleteModal,
  } = useModal({ defaultShow: false });

  const showEditResultsBtn = useMemo(() => {
    const now = new Date();

    return (
      game.hasResults
      || isSameDay(game.date, now)
      || isAfter(startOfDay(now), game.date)
    );
  }, [game]);

  const onShowResulClicked = useCallback(() => {
    if (onShowResultClick) {
      onShowResultClick(game.id);
    }
  }, [game, onShowResultClick]);

  const deleteClicked = useCallback(() => {
    openDeleteModal()
  }, [openDeleteModal]);

  const cancelDeleteEvent = useCallback(() => {
    closeDeleteModal();
  }, [closeDeleteModal]);

  const confirmDeleteEvent = useCallback(() => {
    if (onDeleteClick) {
      onDeleteClick(game.id);
    }
    closeDeleteModal();
  }, [game, onDeleteClick, closeDeleteModal]);

  const gameTeams = game.teams.filter(Boolean);

  return (
    <S.GameCardContainer {...containerProps}>
      <S.GameCardTime>
        {format(props.game.date, 'kk:mm')}
      </S.GameCardTime>

      <S.GameCardTeams>
        <GameTeamInfo
          className="left"
          team={gameTeams[0]}
        />
        <TeamsDelimiter />
        <GameTeamInfo
          className="right"
          team={gameTeams[1]}
        />
      </S.GameCardTeams>

      <S.GameActions>
        {showEditResultsBtn && (
          <S.IconButton
            className='game-action-btn'
            onClick={onShowResulClicked}
          >
            <CupIcon width={14} height={16} />
          </S.IconButton>
        )}

        <S.IconButton
          className='game-action-btn'
          onClick={() => onEditClick(game)}
        >
          <EditIcon width={16} height={16} />
        </S.IconButton>

        <S.IconButton
          className='game-action-btn'
          onClick={deleteClicked}
        >
          <TrashIcon width={14} height={16} />
        </S.IconButton>
      </S.GameActions>

      <ConfirmDeleteModal
        header={{title: 'Подтвердить удаление', subtitle: 'Вы действительно хотите удалить матч?'}}
      >
        <div className='mt-10 d-flex justify-content-between'>
          <UDButton
            variant='secondary'
            className='mb-3'
            type='button'
            onClick={cancelDeleteEvent}
          >
            Отмена
          </UDButton>

          <UDButton
            variant='primary'
            className='mb-3'
            type='button'
            onClick={confirmDeleteEvent}
          >
            Удалить
          </UDButton>
        </div>
      </ConfirmDeleteModal>
    </S.GameCardContainer>
  );
};

export default GamesComponentsGameCard;
